import "katex/dist/katex.min.css";

import type { FC } from "react";
import { useEffect, useState } from "react";
import { ButtonGroup, ToggleButton } from "react-bootstrap";
import Latex from "react-latex-next";

import { TutoringFooter } from "./components/TutoringFooter";
import { TutoringHeader } from "./components/TutoringHeader";

import { ErrorModal } from "components";
import AnswerTutoring from "components/Answer/AnswerTutoring";
import { FirstIncorrectAnswerModal } from "components/Modal/IncorrectAnswer/FirstIncorrectAnswer";
import { SecondIncorrectAnswerModal } from "components/Modal/IncorrectAnswer/SecondIncorrectAnswer";
import { BooleanWithTypeGuard } from "utils/types/BooleanWithTypeGuard";

import type { CheckTutoringAnswerMutation, UserAssignmentDetailFragment } from "api/generated";
import { useCheckTutoringAnswerMutation } from "api/generated";

import { useAppDispatch, useAppSelector } from "store/hooks";
import { processExerciseError } from "utils/processApiError";

import { getAssignmentTime, resetAssignmentTimer, startAssignmentTimer, stopAssignmentTimer } from "store/slices/timer";
import { getTimeToLearn } from "utils/getTimeToLearn";
import { useTutoringContext } from "./context/TutoringContext";

interface Props {
  data: UserAssignmentDetailFragment;
  userAssignmentId: string;
  onNextClick: () => void;
}

const Razeni: FC<Props> = ({ data, userAssignmentId, onNextClick }) => {
  const questionsCount = data.assignment?.assignment?.questions.length ?? 0;
  const options =
    data.assignment?.assignment?.questions.map(question => question.options.map(option => option.value)) ?? [];

  const dispatch = useAppDispatch();
  const [answers, setAnswers] = useState<string[]>(new Array(questionsCount).fill(" ".repeat(options[0].length)));
  const [apiResults, setApiResults] = useState<boolean[]>([]);
  const time = useAppSelector(getAssignmentTime);

  const [sendAnswers, { error: apiMutationError, isLoading: isSendingAnswers }] = useCheckTutoringAnswerMutation();

  const [isErrorModalVisible, setErrorModalVisible] = useState(false);
  const [attempt, setAttempt] = useState(0);
  const { videoTips, tutoringId, claimEvaluation } = useTutoringContext();
  const [activeAccordion, setAccordionActive] = useState<string | string[] | undefined | null>(null);

  useEffect(() => {
    dispatch(resetAssignmentTimer());
    dispatch(startAssignmentTimer());

    return () => {
      dispatch(resetAssignmentTimer());
    };
  }, []);

  useEffect(() => {
    if (apiMutationError === undefined) return;

    processExerciseError(apiMutationError, {
      assignmentId: userAssignmentId,
      selfEvaluation: null,
      userAnswers: answers ?? [],
      timeToSolve: time,
    });
    setErrorModalVisible(true);
  }, [apiMutationError]);

  const processAnswerResponse = (response: { data: CheckTutoringAnswerMutation }) => {
    const { questionResults } = response.data.tutoringAnswer;

    if (!questionResults) return;

    const answersCopy = [...(answers ?? [])];
    questionResults.forEach((result, index) => {
      if (!result) answersCopy[index] = " ".repeat(options[0].length);
    });

    setAnswers(answersCopy);
  };

  const onSubmit = () => {
    setAttempt(prev => prev + 1);

    sendAnswers({
      tutoringId,
      userAssignmentId,
      selfEvaluation: null,
      userAnswers: answers.map(answer => ({
        answers: [answer ?? ""],
      })),
      timeToSolve: time,
    })
      .then(response => {
        if ("data" in response && response.data) {
          setApiResults(response.data.tutoringAnswer.questionResults ?? []);
          processAnswerResponse(response);
          setErrorModalVisible(false);

          setAccordionActive(prev => {
            if (prev) return prev;
            const results = response.data.tutoringAnswer.questionResults ?? [];
            return results.length > 0 && !results.every(result => result) ? "0" : null;
          });
        }
      })
      .catch(error => {
        console.error(error);
      })
      .finally(() => {
        dispatch(resetAssignmentTimer());
        dispatch(startAssignmentTimer());
      });
  };

  const handleNextClick = () => {
    dispatch(stopAssignmentTimer());
    onNextClick();
  };

  const handleCheck = (index: number, position: number, newValue?: string | null) => {
    if (typeof newValue !== "string") return;

    const copy = [...answers];
    const currentQuestion = copy[index].split("");
    currentQuestion[position] = newValue;
    copy[index] = currentQuestion.join("");

    setAnswers(copy);
  };

  useEffect(() => {
    const areAllCorrect = apiResults.length > 0 && apiResults.every(result => result);
    if (areAllCorrect) {
      claimEvaluation({ tutoringId, userAssignmentId, userAnswers: apiResults }).catch(() => {});
    }
  }, [answers]);

  const areAnswersFilled = () =>
    answers.length > 0 && answers.every(answer => answer.split("").every(char => char !== " "));

  const toggleAccordion = (state: boolean) => {
    setAccordionActive(state ? "0" : null);
  };

  const videoIds =
    data.assignment?.analysis?.hasSharedAnalysis === true
      ? [data.assignment.analysis.sharedAnalysis?.explainerVideo].filter(BooleanWithTypeGuard)
      : [
          ...new Set(
            data.assignment?.assignment?.questions
              .map(question => question.solutionAnalysis?.explainerVideo)
              .filter(BooleanWithTypeGuard),
          ),
        ];

  return (
    <div className="exercise__body">
      <TutoringHeader
        data={data}
        userAssignmentId={userAssignmentId}
        activeAccordion={activeAccordion}
        toggleAccordion={toggleAccordion}
        videoIds={videoIds}
        videoTips={videoTips}
      />

      <div>
        {data.assignment?.assignment?.questions.map((question, qIndex) => (
          <div key={`wrapper-${qIndex}`}>
            <Latex>{question.heading ?? ""}</Latex>

            {question.options.map((option, oIndex) => (
              <div
                key={`text-${qIndex}-${oIndex}`}
                className="assignment-option"
                dangerouslySetInnerHTML={{ __html: option.additionalText ?? "" }}
              />
            ))}

            <div
              className={`question question--list mt-4 ${apiResults.at(qIndex) === false ? "question--incorrect" : ""}`}
              {...(qIndex === 0 ? { id: "question" } : {})}
            >
              <>
                {options[qIndex].map((_values, vIndex) => {
                  if (!apiResults[qIndex]) {
                    return (
                      <div key={`question-${qIndex}-${vIndex}`} className="button-group">
                        {answers.length > 0 ? (
                          <>
                            <span>{`${vIndex + 1}.`}</span>
                            <ButtonGroup className={"btn-group-select btn-shadow"}>
                              {options[qIndex].map((option, rIndex) => {
                                const currentValue = answers[qIndex][vIndex] ?? "";
                                return (
                                  <ToggleButton
                                    key={rIndex}
                                    id={`radio-${qIndex}-${vIndex}-${rIndex}`}
                                    type="radio"
                                    variant="select"
                                    value={option ?? ""}
                                    checked={currentValue === option}
                                    onChange={() => {
                                      handleCheck(qIndex, vIndex, option);
                                    }}
                                  >
                                    {option}
                                  </ToggleButton>
                                );
                              })}
                            </ButtonGroup>
                          </>
                        ) : null}
                      </div>
                    );
                  }

                  const selectedString = qIndex in answers ? answers[qIndex] : "";

                  if (data.assignment === undefined || data.assignment === null) return null;

                  return (
                    <div key={`answer-${vIndex}`} className="button-group">
                      <span>{`${vIndex + 1}.`}</span>
                      <AnswerTutoring
                        subject={data.subject?.appName}
                        key={`answer-${qIndex}`}
                        index={qIndex}
                        assignment={data.assignment}
                        userAssignmentId={data.id}
                        type={apiResults?.[qIndex] ? "success" : "danger"}
                        timeToLearn={getTimeToLearn(data.timeToLearn)}
                      >
                        {selectedString[vIndex]}
                      </AnswerTutoring>
                    </div>
                  );
                })}
              </>
            </div>
          </div>
        ))}

        <ErrorModal
          onCancel={() => {
            setErrorModalVisible(false);
          }}
          onHide={() => {
            setErrorModalVisible(false);
          }}
          onRetry={() => {
            onSubmit();
          }}
          show={isErrorModalVisible}
        />

        {!isSendingAnswers && apiResults.includes(false) && (
          <>
            {!videoIds?.length || attempt === 1 ? (
              <FirstIncorrectAnswerModal
                incorrectCount={apiResults.filter(result => !result).length}
                questionsCount={questionsCount}
              />
            ) : null}

            {videoIds?.length && attempt > 1 ? (
              <SecondIncorrectAnswerModal videoIds={videoIds} attempt={attempt} />
            ) : null}
          </>
        )}

        <TutoringFooter
          onSubmit={onSubmit}
          isSubmitDisabled={!areAnswersFilled() || isSendingAnswers}
          isAnalysis={apiResults.length > 0 && apiResults.every(result => result)}
          goNext={handleNextClick}
        />
      </div>
    </div>
  );
};

export default Razeni;
